import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageCircle, Loader2, ExternalLink, Headphones, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OyonoRobot from './OyonoRobot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OyonoChat = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [mood, setMood] = useState('happy');
  const [showContactOptions, setShowContactOptions] = useState(false);
  const [sessionId] = useState(() => `oyono_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initial greeting when chat opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const greeting = {
        id: Date.now(),
        type: 'bot',
        text: t('oyono.greeting', "Hello! 👋 I'm OYONO, your friendly Onerik assistant! How can I help you today?"),
        timestamp: new Date()
      };
      setMessages([greeting]);
    }
    // Reset unread count when opening
    if (isOpen && !isMinimized) {
      setUnreadCount(0);
    }
  }, [isOpen, isMinimized, messages.length, t]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const addBotMessage = (text) => {
    const botMessage = {
      id: Date.now() + 1,
      type: 'bot',
      text: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, botMessage]);
    setIsTyping(false);
    setMood('happy');
    
    // Increment unread if minimized
    if (isMinimized) {
      setUnreadCount(prev => prev + 1);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setMood('thinking');

    // Always use AI for natural conversation
    try {
      const response = await axios.post(`${API}/support/chat`, {
        message: userMessage.text,
        session_id: sessionId,
        language: i18n.language || 'en'
      });

      addBotMessage(response.data.response);
    } catch (error) {
      console.error('Chat error:', error);
      addBotMessage(t('oyono.error', "Oops! My circuits got a bit tangled! 🔧 Try again, or ping our human team on Telegram!"));
      setMood('confused');
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const openTelegram = () => {
    window.open('https://t.me/onerik_support', '_blank');
  };

  const requestLiveChat = async () => {
    setShowContactOptions(false);
    const systemMessage = {
      id: Date.now(),
      type: 'system',
      text: t('oyono.liveRequest', "🎧 Live chat requested! A human support agent will join shortly. Please describe your issue while you wait."),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, systemMessage]);
    
    // Notify backend about live chat request
    try {
      await axios.post(`${API}/support/live-request`, {
        session_id: sessionId,
        messages: messages.map(m => ({ type: m.type, text: m.text }))
      });
    } catch (error) {
      console.error('Live chat request error:', error);
    }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="oyono-chat-button"
          className="fixed bottom-5 right-5 z-50 group"
        >
          <div className="relative">
            {/* Pulse ring */}
            <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" style={{ animationDuration: '3s' }} />
            
            {/* Robot container - matches page background */}
            <div className="relative bg-[#050505]/95 backdrop-blur-xl rounded-xl p-2 shadow-xl border border-white/5 hover:border-primary/30 transition-all duration-300 hover:scale-105">
              <OyonoRobot size="small" mood="happy" />
              
              {/* Speech bubble */}
              <div className="absolute -top-8 -left-2 bg-[#0a0a0a] text-white px-2 py-1 rounded-lg text-[10px] font-medium shadow-lg border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
                {t('oyono.bubble', "Hey! Chat? 💬")}
                <div className="absolute -bottom-1 left-3 w-2 h-2 bg-[#0a0a0a] border-r border-b border-white/10 transform rotate-45" />
              </div>
            </div>
            
            {/* Online indicator */}
            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#050505] animate-pulse" />
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 z-50 bg-[#050505]/98 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/5 flex flex-col overflow-hidden animate-in slide-in-from-bottom-5 duration-300 transition-all ${
            isMinimized 
              ? 'w-[280px] h-[60px]' 
              : 'w-[340px] max-w-[calc(100vw-48px)] h-[420px] max-h-[calc(100vh-100px)]'
          }`}
          data-testid="oyono-chat-window"
        >
          {/* Header */}
          <div className={`bg-black/40 px-3 py-2 flex items-center justify-between border-b border-white/5 ${isMinimized ? 'cursor-pointer' : ''}`}
               onClick={() => isMinimized && setIsMinimized(false)}>
            <div className="flex items-center gap-2">
              <div className="relative">
                <OyonoRobot size="small" mood={mood} speaking={isTyping} />
                <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[#050505]" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xs">OYONO</h3>
                <p className="text-green-400 text-[10px]">{t('oyono.status', 'Online')}</p>
              </div>
              {/* Unread badge when minimized */}
              {isMinimized && unreadCount > 0 && (
                <span className="bg-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-2">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {!isMinimized && (
                <button
                  onClick={() => setShowContactOptions(!showContactOptions)}
                  className="p-1.5 hover:bg-white/5 rounded-lg transition-colors text-white/60 hover:text-white"
                  title={t('oyono.contactHuman', 'Contact Human Support')}
                >
                  <Headphones className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMinimized(!isMinimized);
                  if (!isMinimized) setUnreadCount(0);
                }}
                className="p-1.5 hover:bg-white/5 rounded-lg transition-colors text-white/60 hover:text-white"
                title={isMinimized ? t('oyono.expand', 'Expand') : t('oyono.minimize', 'Minimize')}
              >
                <Minus className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setIsMinimized(false);
                }}
                className="p-1.5 hover:bg-white/5 rounded-lg transition-colors text-white/60 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Contact Options Dropdown */}
          {showContactOptions && !isMinimized && (
            <div className="absolute top-14 right-4 bg-[#0a0a0a] border border-white/10 rounded-xl p-2 shadow-xl z-10 animate-in fade-in duration-200">
              <p className="text-white/60 text-[10px] mb-1.5 px-2">{t('oyono.contactOptions', 'Contact Human Support')}</p>
              <button
                onClick={openTelegram}
                className="w-full flex items-center gap-2 px-2 py-1.5 bg-[#0088cc]/10 hover:bg-[#0088cc]/20 text-[#0088cc] rounded-lg text-xs transition-colors mb-1"
              >
                <ExternalLink className="w-3 h-3" />
                Telegram
              </button>
              <button
                onClick={requestLiveChat}
                className="w-full flex items-center gap-2 px-2 py-1.5 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg text-xs transition-colors"
              >
                <MessageCircle className="w-3 h-3" />
                {t('oyono.liveChat', 'Live Chat')}
              </button>
            </div>
          )}

          {/* Messages - Hidden when minimized */}
          {!isMinimized && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {message.type === 'system' ? (
                      <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-xs px-3 py-1.5 rounded-xl max-w-[90%]">
                        {message.text}
                      </div>
                    ) : (
                      <div
                        className={`max-w-[85%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${
                          message.type === 'user'
                            ? 'bg-primary text-white rounded-br-md'
                            : 'bg-white/5 text-white rounded-bl-md border border-white/5'
                        }`}
                      >
                        {message.text}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Typing indicator */}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white/5 border border-white/5 px-3 py-2 rounded-2xl rounded-bl-md">
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-2 border-t border-white/5">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={t('oyono.placeholder', 'Say anything to OYONO...')}
                    className="flex-1 bg-white/5 border border-white/5 focus:border-primary/30 rounded-xl px-3 py-2 text-xs text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all"
                    data-testid="oyono-input"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || isTyping}
                    className="bg-primary hover:bg-primary/90 disabled:bg-white/5 disabled:cursor-not-allowed text-white p-2 rounded-xl transition-all hover:scale-105 active:scale-95 disabled:hover:scale-100"
                    data-testid="oyono-send"
                  >
                    {isTyping ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-center text-white/20 text-[8px] mt-1">
                  {t('oyono.powered', 'Powered by OYONO AI')} 🤖
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default OyonoChat;
